import {
  Card,
  ECardBorderRadiusSize,
  ETagVariant,
  GeneralStarFillIcon,
  Tag,
} from '@outdoorsyco/bonfire';
import { clsx } from 'clsx';
import escapeHtml from 'escape-html';
import React, {
  AnchorHTMLAttributes,
  HTMLAttributeAnchorTarget,
  useCallback,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import Anchor from '@/components/switchback/Anchor/Anchor';
import Link from '@/components/switchback/Link/Link';
import { IWishlistingEventData } from '@/services/analytics/listings/types';
import { ISearchResultTile } from '@/utility/mapSearchResultToTile';

import { HorizontalListingTileContent } from './HorizontalListingTileContent';
import { ListingTileContent } from './ListingTileContent';
import { ListingTileMedia } from './ListingTileMedia';

export enum EListingTileVariant {
  Horizontal = 'horizontal',
  HorizontalBorderless = 'horizontal-borderless',
  Vertical = 'vertical',
}

type TListingTileProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  rentalTile: ISearchResultTile;
  isOutlined?: boolean;
  showFirstImageOnly?: boolean;
  onSlideChange?: (nextIndex: number) => void;
  variant?: EListingTileVariant;
  displayStationaryCamperPromo?: boolean;
  customRentalTitle?: string;
  showProBadge?: boolean;
  showSuperhost?: boolean;
  showGuestFavorite?: boolean;
  withPriority?: boolean;
  withFetchPriority?: 'high' | 'low' | undefined;
  isNewHomepage?: boolean;
  target?: HTMLAttributeAnchorTarget | undefined;
  dataTestId?: string;
  ssrNavigation?: boolean;
  wishlistingEventData?: IWishlistingEventData;
  autoCssJudgeStyle?: boolean; // Will use css media query instead state update
  isEscapesSearchPage?: boolean;
  isFeatured?: boolean;
  showQuickPreview?: boolean;
  quickPreviewOnClickViewListing?: () => void;
  customWishlistName?: string;
};

export const ListingTile = ({
  rentalTile,
  isOutlined,
  showFirstImageOnly,
  onSlideChange,
  variant = EListingTileVariant.Vertical,
  displayStationaryCamperPromo,
  customRentalTitle = '',
  showSuperhost = false,
  showGuestFavorite = false,
  showProBadge = false,
  withPriority = false,
  withFetchPriority,
  isNewHomepage = false,
  target,
  dataTestId,
  ssrNavigation = true,
  wishlistingEventData,
  autoCssJudgeStyle = false,
  isEscapesSearchPage = false,
  isFeatured = false,
  showQuickPreview = false,
  quickPreviewOnClickViewListing,
  customWishlistName,
  ...props
}: TListingTileProps) => {
  const escapedTitle = rentalTile.title && escapeHtml(rentalTile.title);
  const isHorizontal = variant === EListingTileVariant.Horizontal;
  const isHorizontalBorderless = variant === EListingTileVariant.HorizontalBorderless;
  const intl = useIntl();
  const [isHovered, setIsHovered] = useState(false);

  const { onMouseEnter, onMouseLeave } = props;
  const handleMouseEnterLeave = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (event.type === 'mouseenter') {
        onMouseEnter && onMouseEnter(event);
        setIsHovered(true);
      } else {
        onMouseLeave && onMouseLeave(event);
        setIsHovered(false);
      }
    },
    [onMouseEnter, onMouseLeave],
  );

  let card = (
    <Card
      borderRadiusSize={ECardBorderRadiusSize.Large}
      showHoverEffect={false}
      withBoxShadow={false}
      withTransparentBackground={true}
      aria-label={displayStationaryCamperPromo ? customRentalTitle : escapedTitle}
      title={displayStationaryCamperPromo ? customRentalTitle : escapedTitle}
      className={clsx({
        '!border-neutral-20 !border-solid !border': isHorizontal,
        'outline outline-2 outline-offset-2 outline-green-500': isOutlined && isHorizontal,
        '!border-gray-500 -m-2 p-2 !rounded-lg !rounded-tr-[28px] relative':
          isFeatured && isHorizontal,
        '!border-gray-310 !border -m-2 p-2 !rounded-lg !rounded-tr-[28px] relative':
          isFeatured && !isHorizontal,
      })}
      {...props}>
      <div
        className={clsx('flex', {
          'flex-row': isHorizontal || isHorizontalBorderless,
          'flex-col': !isHorizontal && !isHorizontalBorderless,
          'gap-4 items-center': isHorizontalBorderless,
          '!flex-row md:!flex-col !gap-4 !items-center': autoCssJudgeStyle,
          relative: isFeatured,
        })}>
        {isFeatured && (
          <div className="absolute top-0 left-0 z-10">
            <Tag
              variant={ETagVariant.White}
              label={intl.formatMessage({ defaultMessage: 'Top pick for your trip', id: 'zFKIAp' })}
              icon={GeneralStarFillIcon}
              className="-m-2 text-white uppercase"
              iconClassName="!text-canvas-300"
            />
          </div>
        )}
        <div
          className={clsx({
            'w-2/5 rounded-l-2xl': isHorizontal,
            'rounded-2xl': !isHorizontal,
          })}>
          <ListingTileMedia
            rentalTile={rentalTile}
            isOutlined={isOutlined && !isHorizontal}
            showFirstImageOnly={showFirstImageOnly}
            onSlideChange={onSlideChange}
            showLimitedOfferTag={displayStationaryCamperPromo}
            isHorizontal={isHorizontal}
            isHorizontalBorderless={isHorizontalBorderless}
            showSuperhost={showSuperhost}
            showGuestFavorite={showGuestFavorite}
            withPriority={withPriority}
            withFetchPriority={withFetchPriority}
            isNewHomepage={isNewHomepage}
            wishlistingEventData={wishlistingEventData}
            autoCssJudgeStyle={autoCssJudgeStyle}
            isFeatured={isFeatured}
            tileIsHovered={isHovered}
            showQuickPreview={showQuickPreview}
            quickPreviewOnClickViewListing={quickPreviewOnClickViewListing}
            quickPreviewOnClose={() => setIsHovered(false)}
            customWishlistName={customWishlistName}
          />
        </div>

        {isHorizontal ? (
          <HorizontalListingTileContent
            rentalTile={rentalTile}
            customTitle={displayStationaryCamperPromo ? customRentalTitle : ''}
          />
        ) : (
          <ListingTileContent
            rentalTile={rentalTile}
            customTitle={displayStationaryCamperPromo ? customRentalTitle : ''}
            showProBadge={showProBadge}
          />
        )}
      </div>
    </Card>
  );

  if (displayStationaryCamperPromo) {
    card = <div className="p-4 bg-green-30 rounded-2xl">{card}</div>;
  }

  if (rentalTile.linkUrl) {
    // On the localities page, using the browser's back button on mobile results in a 404 error.
    // This issue occurs because client-side navigation does not properly handle certain routes.
    // Refreshing the page resolves the issue since it triggers a server-side request.
    // To address this, we use server-side navigation (`<a>` tag) instead of client-side navigation (`<Link>` component)
    // when the `ssrNavigation` flag is set.
    const linkUrl =
      'escapeLinkUrl' in rentalTile && isEscapesSearchPage
        ? rentalTile.escapeLinkUrl
        : rentalTile.linkUrl;
    return (
      <Link href={linkUrl} legacy passHref forceClientsideRouting={!ssrNavigation}>
        <Anchor
          href={linkUrl}
          target={target}
          data-testid={dataTestId}
          onClick={props.onClick}
          onMouseEnter={handleMouseEnterLeave}
          onMouseLeave={handleMouseEnterLeave}
          referrer-policy="no-referrer-when-downgrade"
          rel="noopener">
          {card}
        </Anchor>
      </Link>
    );
  }

  return card;
};
